/* eslint-disable max-lines */
import React, { useContext, useState, createRef } from 'react';
import { observer } from 'mobx-react';
import { styled } from '@this/constants/themes';
import type { Moment } from 'moment';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import AppointContext from '@this/components/appoint/appoint_context';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import PlaceIcon from '@material-ui/icons/Place';
import VideocamIcon from '@material-ui/icons/Videocam';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import SubjectIcon from '@material-ui/icons/Subject';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import muiTheme from '@this/components/appoint/mui_theme';
import Utils from '@this/domain/appoint/utils';
import { ButtonWrapper, ButtonProgress } from '../../common';

export interface DestinationInfo {
  name: string;
  email: string;
}

interface Props {
  show: boolean;
  onClose?: () => void;
  title: string;
  startTime: Moment | undefined;
  endTime: Moment | undefined;
  meetingType: string;
  onlineMeetingName: string;
  onlineMeetingUrl: string;
  placeName: string;
  description: string;
  onSubmit: (share_destinations: DestinationInfo[]) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}

const FixedAdjustmentDialog = observer(
  ({
    show,
    onClose,
    title,
    startTime,
    endTime,
    meetingType,
    onlineMeetingName,
    onlineMeetingUrl,
    placeName,
    description,
    onSubmit,
    isSubmitting,
    errorMessage
  }: Props) => {
    const contextValue = useContext(AppointContext);
    const [destinationName1, setDestinationName1] = useState<string>('');
    const [destinationName2, setDestinationName2] = useState<string>('');
    const [destinationName3, setDestinationName3] = useState<string>('');
    const [destinationMailAddress1, setDestinationMailAddress1] = useState<string>('');
    const [destinationMailAddress2, setDestinationMailAddress2] = useState<string>('');
    const [destinationMailAddress3, setDestinationMailAddress3] = useState<string>('');

    const isInputShareDestination = () => {
      const legAddress = /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}.[A-Za-z0-9]{1,}$/;
      if (
        destinationName1.length === 0 ||
        destinationMailAddress1.length === 0 ||
        !legAddress.test(destinationMailAddress1)
      ) {
        return false;
      }
      if (
        (destinationName2.length > 0 && destinationMailAddress2.length === 0) ||
        (destinationName2.length === 0 && destinationMailAddress2.length > 0) ||
        (destinationName2.length > 0 &&
          destinationMailAddress2.length > 0 &&
          !legAddress.test(destinationMailAddress2))
      ) {
        return false;
      }
      if (
        (destinationName3.length > 0 && destinationMailAddress3.length === 0) ||
        (destinationName3.length === 0 && destinationMailAddress3.length > 0) ||
        (destinationName3.length > 0 &&
          destinationMailAddress3.length > 0 &&
          !legAddress.test(destinationMailAddress3))
      ) {
        return false;
      }
      return true;
    };

    const handleScheduleShare = async () => {
      if (!isInputShareDestination) {
        return;
      }
      // todo: share_destinationsに複数格納可とする
      const share_destinations: DestinationInfo[] = [
        { name: destinationName1, email: destinationMailAddress1 },
        { name: destinationName2, email: destinationMailAddress2 },
        { name: destinationName3, email: destinationMailAddress3 }
      ];
      onSubmit(share_destinations);
    };

    const onlineMeetingUrlRef = createRef<HTMLDivElement>();
    const copyOnlineMeetingUrlToClipBoard = () => {
      Utils.copyElementToClipBoard(onlineMeetingUrlRef.current, false);
      contextValue.setSnackbarContent({
        message: 'Web会議リンクをクリップボードにコピーしました',
        severity: 'success'
      });
      contextValue.setOpenSnackbar(true);
    };

    return (
      <TheDialog
        open={show}
        onClose={onClose}
        maxWidth="md"
        style={contextValue.isMobile ? { zIndex: 99 } : {}}
        PaperProps={{
          style: contextValue.isMobile
            ? {
                width: '100%',
                margin: '15px',
                boxShadow: '0 0'
              }
            : {}
        }}
        isMobile={contextValue.isMobile}
      >
        <FixedPaper isMobile={contextValue.isMobile} style={contextValue.isMobile ? { paddingTop: '15px' } : {}}>
          <FixedHead>
            <FixedHeadIcon>
              <ThumbUpIcon fontSize="large" />
            </FixedHeadIcon>
            <FixedText>\ ご対応ありがとうございます /</FixedText>
            <FixedTitle>日程調整が完了しました</FixedTitle>
          </FixedHead>
          <Box>
            <FixedContent isMobile={contextValue.isMobile}>
              <Box display="flex" style={{ marginBottom: '20px', alignItems: 'center' }}>
                <FixedInfoTitle>{title}</FixedInfoTitle>
              </Box>
              <FixedInfoBox isMobile={contextValue.isMobile}>
                <TheInfoItem isMobile={contextValue.isMobile}>
                  <FixedInfoLabel>
                    <TheInfoIcon>
                      <CalendarTodayIcon fontSize="small" />
                    </TheInfoIcon>
                    <span>日程</span>
                  </FixedInfoLabel>
                  <FixedInfoText>{startTime?.format('MM月DD日 (ddd)')}</FixedInfoText>
                </TheInfoItem>
                <TheInfoItem isMobile={contextValue.isMobile}>
                  <FixedInfoLabel>
                    <TheInfoIcon>
                      <AccessTimeIcon fontSize="small" />
                    </TheInfoIcon>
                    <span>時間</span>
                  </FixedInfoLabel>
                  <FixedInfoText>
                    {startTime && endTime ? (
                      <>
                        {startTime.format('HH:mm 〜 ')}
                        {endTime.format('HH:mm')}
                      </>
                    ) : (
                      <span style={{ fontSize: '10px' }}>日程が確定されていません</span>
                    )}
                  </FixedInfoText>
                </TheInfoItem>
                <TheInfoItem isMobile={contextValue.isMobile}>
                  <FixedInfoLabel>
                    <TheInfoIcon>
                      <PlaceIcon fontSize="small" />
                    </TheInfoIcon>
                    <span>場所</span>
                  </FixedInfoLabel>
                  <FixedInfoText>{meetingType && meetingType !== 'none' ? 'オンライン' : placeName}</FixedInfoText>
                </TheInfoItem>
                {onlineMeetingUrl === null ? (
                  <TheInfoItem isMobile={contextValue.isMobile}>
                    <FixedInfoLabel>
                      <TheInfoIcon>
                        <VideocamIcon fontSize="small" />
                      </TheInfoIcon>
                      <span>Web会議</span>
                    </FixedInfoLabel>
                    <FixedInfoText>{onlineMeetingName}</FixedInfoText>
                  </TheInfoItem>
                ) : (
                  <>
                    <TheInfoItem isMobile={contextValue.isMobile} style={{ width: '380px' }}>
                      <FixedInfoLabel>
                        <span>Web会議</span>
                      </FixedInfoLabel>
                      <FixedInfoText
                        style={{ color: muiTheme.palette.primary.main, width: '300px', textAlign: 'right' }}
                      >
                        URLを開く
                        <OpenInNewIcon
                          fontSize="small"
                          style={{ cursor: 'pointer' }}
                          onClick={() => window.open(onlineMeetingUrl, '_blank')}
                        />
                      </FixedInfoText>
                    </TheInfoItem>
                    <TheInfoItem
                      isMobile={contextValue.isMobile}
                      style={{ width: contextValue.isMobile ? '100%' : '380px' }}
                    >
                      <FixedInfoLabel style={{ color: muiTheme.palette.primary.main }}>
                        <TheInfoIcon>
                          <VideocamIcon fontSize="small" />
                        </TheInfoIcon>
                        {onlineMeetingName}
                      </FixedInfoLabel>
                      <FixedInfoText style={{ color: muiTheme.palette.primary.main }}>
                        <OnlineMeetingUrl>
                          <OnlineMeetingUrlText ref={onlineMeetingUrlRef}>{onlineMeetingUrl}</OnlineMeetingUrlText>
                          <CopyIconWrap>
                            <CopyIconButton
                              edge="end"
                              color="primary"
                              aria-label="copy"
                              onClick={() => copyOnlineMeetingUrlToClipBoard()}
                            >
                              <FilterNoneIcon fontSize="small" style={{ color: '#fff' }} />
                            </CopyIconButton>
                          </CopyIconWrap>
                        </OnlineMeetingUrl>
                      </FixedInfoText>
                    </TheInfoItem>
                  </>
                )}
                <TheInfoItem isMobile={contextValue.isMobile} style={{ display: 'block', width: '100%' }}>
                  <FixedInfoLabel>
                    <TheInfoIcon>
                      <SubjectIcon fontSize="small" />
                    </TheInfoIcon>
                    <span>メモ</span>
                  </FixedInfoLabel>
                  <FixedInfoNote>
                    {description?.split('\n').map((line, i) => (
                      <Typography key={i} style={{ fontSize: '12px' }}>
                        {line}
                      </Typography>
                    ))}
                  </FixedInfoNote>
                </TheInfoItem>
              </FixedInfoBox>
              <ShareUsesrListBox isMobile={contextValue.isMobile}>
                <ShareUsesrListText isMobile={contextValue.isMobile} style={{ marginBottom: '15px' }}>
                  予定を共有したい方のメールアドレスを入力してください。
                </ShareUsesrListText>
                <table>
                  <tr style={{ textAlign: 'left' }}>
                    <th>
                      <ShareUsesrListLabel isMobile={contextValue.isMobile}>お名前</ShareUsesrListLabel>
                    </th>
                    <th>
                      <ShareUsesrListLabel isMobile={contextValue.isMobile}>メールアドレス</ShareUsesrListLabel>
                    </th>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: '20px' }}>
                      <TheTextField
                        value={destinationName1}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setDestinationName1(e.target.value)}
                      />
                    </td>
                    <td>
                      <TheTextField
                        value={destinationMailAddress1}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setDestinationMailAddress1(e.target.value)}
                        style={{ width: '200px' }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: '20px' }}>
                      <TheTextField
                        value={destinationName2}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setDestinationName2(e.target.value)}
                      />
                    </td>
                    <td>
                      <TheTextField
                        value={destinationMailAddress2}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setDestinationMailAddress2(e.target.value)}
                        style={{ width: '200px' }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: '20px' }}>
                      <TheTextField
                        value={destinationName3}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setDestinationName3(e.target.value)}
                      />
                    </td>
                    <td>
                      <TheTextField
                        value={destinationMailAddress3}
                        InputLabelProps={{ shrink: true }}
                        onChange={e => setDestinationMailAddress3(e.target.value)}
                        style={{ width: '200px' }}
                      />
                    </td>
                  </tr>
                </table>
              </ShareUsesrListBox>
              <Box style={{ marginLeft: '20px', display: 'none' }}>
                <ButtonIcon>
                  <AddCircleOutlineIcon style={{ color: muiTheme.palette.primary.main }} fontSize="large" />
                </ButtonIcon>
                <Button variant="contained" color="secondary" style={{ margin: '0 auto' }} disabled>
                  追加
                </Button>
              </Box>
            </FixedContent>
            <FixedContentButtonWrap>
              <TheButtonWrapper isMobile={contextValue.isMobile}>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ margin: '0 auto', width: '180px' }}
                  disabled={!isInputShareDestination() || isSubmitting}
                  onClick={() => handleScheduleShare()}
                >
                  この予定を共有する
                </Button>
                {isSubmitting && <ButtonProgress />}
                {errorMessage && <Error color="error">{errorMessage}</Error>}
                {contextValue.user && (
                  <Button
                    variant="outlined"
                    color="primary"
                    style={{ margin: '0 auto 0 10px', width: '180px' }}
                    href="/aicalendar"
                  >
                    カレンダーに戻る
                  </Button>
                )}
              </TheButtonWrapper>
            </FixedContentButtonWrap>
            {!contextValue.user && (
              <FixedFoot style={{ padding: contextValue?.isMobile ? '0px 0px' : '20px 12px' }}>
                <FixedFootHead style={{ marginLeft: contextValue?.isMobile ? '35px' : '75px' }}>
                  <FixedFootImg>
                    <img src="/images/aicalendar/img_signup_banner01.png" width="174" height="88" />
                  </FixedFootImg>
                  <FixedFootTitle>
                    <FixedFootTitleTop>
                      <img
                        src="/images/aicalendar/aicalendar_logo.png"
                        height="26"
                        style={{ marginRight: '5px', verticalAlign: 'text-bottom' }}
                      />
                      で
                    </FixedFootTitleTop>
                    <FixedFootTitleBelow>
                      スケジュール調整を
                      <br />
                      効率化！
                    </FixedFootTitleBelow>
                  </FixedFootTitle>
                </FixedFootHead>
                <FixedFootText>
                  AIカレンダーはあなたのカレンダーと連携し、日程をサクッと調整できるサービスです。
                </FixedFootText>
                <Button
                  style={{
                    display: 'block',
                    margin: contextValue?.isMobile ? '5px 95px 0 95px' : '5px 0 0 130px'
                  }}
                  onClick={() => {
                    location.href = contextValue.googleOauthPath;
                  }}
                >
                  <img src="/images/sign_in_with_google.png" width="191" />
                </Button>
                <Button
                  style={{ display: 'block', margin: contextValue?.isMobile ? '-5px 0 0 83px' : '-5px 0 0 118px' }}
                  onClick={() => {
                    location.href = contextValue.outlookOauthPath;
                  }}
                >
                  <img src="/images/sign_in_with_microsoft.png" width="215" />
                </Button>
              </FixedFoot>
            )}
          </Box>
        </FixedPaper>
      </TheDialog>
    );
  }
);

const TheDialog = styled(Dialog)<{ isMobile: boolean }>`
  ${props =>
    props.isMobile &&
    `
    overflow-y: auto;

    .MuiBackdrop-root {
      background-color: #ededed;
    }

    .MuiDialog-container.MuiDialog-scrollPaper {
      min-height: 120vh;
      padding-top: 54px;
      align-items: start;
    }
  `}
`;

const FixedHead = styled(Box)`
  margin-bottom: 25px;
  text-align: center;
`;

const FixedHeadIcon = styled.div`
  margin-bottom: 5px;
  color: ${muiTheme.palette.primary.main};
`;

const FixedTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
`;

const FixedText = styled(Typography)`
  color: ${muiTheme.palette.primary.main};
  font-size: 11px;
  font-weight: 700;
`;

const FixedPaper = styled(Paper)<{ isMobile: boolean }>`
  min-width: 500px;
  padding-top: 30px;
  box-sizing: border-box;

  ${props =>
    props.isMobile &&
    `
    min-width: auto;
    max-height: calc(100vh - 54px - 15px - 15px);
    box-shadow: 0 0;
  `}
`;

const FixedContent = styled(Box)<{ isMobile: boolean }>`
  padding: 0 50px 20px;

  ${props =>
    props.isMobile &&
    `
    padding: 0 25px 20px;
  `}
`;

const FixedInfoTitle = styled(Typography)<{ active?: string }>`
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
`;

const FixedInfoBox = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-wrap: wrap;
  place-content: flex-start;
  justify-content: space-between;
  width: 390px;
  background: #f9f9f9;
  border: 1px solid #ededed;
  box-sizing: border-box;
  border-radius: 3px;

  ${props =>
    props.isMobile &&
    `
    display: block;
    width: 100%;
    margin-bottom: 10px;
    background: #fff;
    border: 0;
  `}
`;

const TheInfoItem = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 190px;
  margin-bottom: 10px;
  padding: 6px;

  ${props =>
    props.isMobile &&
    `
    margin-bottom: 0;
  `}
`;

const FixedInfoLabel = styled.div`
  color: #909090;
  font-size: 10px;
`;

const FixedInfoText = styled(Typography)`
  margin-left: 10px;
  font-size: 12px;

  svg {
    vertical-align: middle;
    margin-left: 3px;
    font-size: 13px;
  }
`;

const FixedInfoNote = styled(Box)`
  margin: 10px 0 0;
`;

const FixedContentButtonWrap = styled(Box)`
  padding: 0 40px 20px;
  text-align: center;
`;

const FixedFoot = styled(Box)`
  background: #f9f9f9;
  text-align: center;
`;

const FixedFootHead = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const FixedFootImg = styled(Box)`
  margin-right: 10px;
`;

const FixedFootTitle = styled(Box)``;

const FixedFootTitleTop = styled(Typography)`
  color: ${muiTheme.palette.primary.main};
  font-size: 14px;
  font-weight: bold;
`;

const FixedFootTitleBelow = styled(FixedFootTitleTop)`
  margin-top: 3px;
  font-size: 13px;
`;

const FixedFootText = styled(Typography)`
  font-size: 10px;
`;

const TheInfoIcon = styled.span`
  display: inline-block;
  vertical-align: middle;
  height: 20px;
  margin-right: 4px;
`;

const TheTextField = styled(TextField)`
  font-size: 12px;
`;

const ButtonIcon = styled.span`
  display: inline-block;
  margin-right: 4px;
`;

const CopyIconWrap = styled(Box)`
  min-width: 36px;
  margin-bottom: auto;
  background: #fff;
  box-shadow: -21px 0px 20px 20px #fff;
`;

const CopyIconButton = styled(IconButton)`
  padding: 8px;
  background: ${muiTheme.palette.primary.main};
  box-shadow: 0 0 6px -2px #000;
  border-radius: 50%;
  line-height: 1;
  transform: rotateX(180deg);
`;

const OnlineMeetingUrl = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;

const OnlineMeetingUrlText = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  font-size: 12px;
`;

const ShareUsesrListBox = styled(Box)<{ isMobile: boolean }>`
  margin: 20px 20px 10px 20px;
  width: 347px;

  ${props =>
    props.isMobile &&
    `
    margin: 0;
    width: 100%;
  `}
`;

const ShareUsesrListText = styled(Typography)<{ isMobile: boolean }>`
  font-size: 14px;
  font-weight: bold;

  ${props =>
    props.isMobile &&
    `
    font-size: 12px;
  `}
`;

const ShareUsesrListLabel = styled(Typography)<{ isMobile: boolean }>`
  font-size: 12px;
  font-weight: bold;

  ${props =>
    props.isMobile &&
    `
    font-size: 10px;
  `}
`;

const Error = styled(Typography).attrs(() => ({ color: 'error' }))`
  margin-top: 12px;
  font-size: 10px;
  text-align: center;
`;

const TheButtonWrapper = styled(ButtonWrapper)<{ isMobile: boolean }>`
  box-sizing: border-box;
  width: 100%;
  margin-top: 28px;
  padding: 10px 24px;
  background: #f9f9f9;

  ${props =>
    props.isMobile &&
    `
    display: flex;
    align-items: center;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 200;
    padding: 10px 24px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
  `}
`;

export default FixedAdjustmentDialog;
